/**
 * ReceivedPOsSummaryPopup.jsx
 * 
 * A floating popup showing the last 15 received POs with:
 *   - Date/time received
 *   - Invoice / PO ref
 *   - Number of items in the PO
 *   - Number of sales orders matched (via v_received_po_open_orders)
 * 
 * Usage in ReceivedPOs.jsx:
 *   import ReceivedPOsSummaryPopup from './ReceivedPOsSummaryPopup';
 *   ...
 *   const [showSummary, setShowSummary] = useState(false);
 *   ...
 *   <button className="app-header__btn" onClick={() => setShowSummary(true)}>📋 Recent POs</button>
 *   {showSummary && <ReceivedPOsSummaryPopup onClose={() => setShowSummary(false)} />}
 */

import { useState, useEffect, useCallback } from 'react';
import { supabase } from '../supabaseClient';

// ── Helpers ───────────────────────────────────────────────────────────────────

function fmtDateTime(val) {
  if (!val) return '—';
  const d = new Date(val);
  return d.toLocaleString('en-NZ', {
    day:    '2-digit',
    month:  'short',
    year:   'numeric',
    hour:   '2-digit',
    minute: '2-digit',
    hour12: false,
  });
}

function fmtAge(val) {
  if (!val) return '';
  const diffMs  = Date.now() - new Date(val).getTime();
  const diffMin = Math.floor(diffMs / 60000);
  if (diffMin < 60)   return `${diffMin}m ago`;
  const diffHr = Math.floor(diffMin / 60);
  if (diffHr  < 24)   return `${diffHr}h ago`;
  const diffDay = Math.floor(diffHr / 24);
  return `${diffDay}d ago`;
}

// ── Main component ────────────────────────────────────────────────────────────

export default function ReceivedPOsSummaryPopup({ onClose }) {
  const [rows,    setRows]    = useState([]);
  const [loading, setLoading] = useState(true);
  const [error,   setError]   = useState(null);
  const [loadedAt, setLoadedAt] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      // 1. Last 15 received POs
      const { data: headers, error: hErr } = await supabase
        .from('purchase_order_header')
        .select('order_id, order_ref, supplier_invoice_ref, supplier, fully_received_date')
        .not('fully_received_date', 'is', null)
        .order('fully_received_date', { ascending: false })
        .limit(15);

      if (hErr) throw hErr;
      if (!headers || headers.length === 0) { setRows([]); return; }

      const orderIds = headers.map(h => h.order_id);

      // 2. Item counts per PO
      const { data: items, error: iErr } = await supabase
        .from('purchase_order_items')
        .select('order_id, item_code')
        .in('order_id', orderIds);

      if (iErr) throw iErr;

      // 3. Matched SO count — how many distinct SOs appear in v_received_po_open_orders
      //    for each of these PO refs
      const poRefs = headers.map(h => h.order_ref);
      const { data: soMatches, error: sErr } = await supabase
        .from('v_received_po_open_orders')
        .select('po_ref, order_ref')
        .in('po_ref', poRefs);

      if (sErr) throw sErr;

      // Build lookup maps
      const itemCountByOrderId = {};
      for (const item of (items || [])) {
        itemCountByOrderId[item.order_id] = (itemCountByOrderId[item.order_id] || 0) + 1;
      }

      // Distinct SOs per po_ref
      const sosByPoRef = {};
      for (const match of (soMatches || [])) {
        if (!sosByPoRef[match.po_ref]) sosByPoRef[match.po_ref] = new Set();
        sosByPoRef[match.po_ref].add(match.order_ref);
      }

      const enriched = headers.map(h => ({
        ...h,
        item_count: itemCountByOrderId[h.order_id] || 0,
        so_count:   sosByPoRef[h.order_ref] ? sosByPoRef[h.order_ref].size : 0,
      }));

      setRows(enriched);
      setLoadedAt(new Date());
    } catch (e) {
      setError(e.message || 'Failed to load data');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  // Close on Escape
  useEffect(() => {
    const handler = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [onClose]);

  return (
    <>
      {/* Backdrop */}
      <div
        onClick={onClose}
        style={{
          position:        'fixed',
          inset:           0,
          background:      'rgba(13, 21, 32, 0.55)',
          backdropFilter:  'blur(2px)',
          zIndex:          1000,
        }}
      />

      {/* Popup window */}
      <div style={{
        position:     'fixed',
        top:          '50%',
        left:         '50%',
        transform:    'translate(-50%, -50%)',
        zIndex:       1001,
        width:        960,
        maxWidth:     '95vw',
        maxHeight:    '85vh',
        display:      'flex',
        flexDirection:'column',
        background:   'var(--surface, #151F2E)',
        border:       '1px solid var(--border, #1E2D42)',
        borderRadius: 10,
        boxShadow:    '0 24px 60px rgba(0,0,0,0.55)',
        fontFamily:   'var(--font, Helvetica, sans-serif)',
        overflow:     'hidden',
      }}>

        {/* Header */}
        <div style={{
          display:        'flex',
          alignItems:     'center',
          justifyContent: 'space-between',
          padding:        '14px 20px 12px',
          borderBottom:   '1px solid var(--border, #1E2D42)',
          background:     'var(--surface-raised, #0F1A28)',
          flexShrink:     0,
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <span style={{ fontSize: 18 }}>📦</span>
            <div>
              <div style={{
                fontSize:   15,
                fontWeight: 700,
                color:      'var(--text, #E8EDF4)',
                letterSpacing: 0.2,
              }}>
                Recently Received POs
              </div>
              <div style={{ fontSize: 11, color: 'var(--muted, #6B7D94)', marginTop: 1 }}>
                Last 15 · {loadedAt ? `loaded ${loadedAt.toLocaleTimeString('en-NZ')}` : 'loading…'}
              </div>
            </div>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <button
              onClick={load}
              disabled={loading}
              title="Refresh"
              style={{
                background:   'var(--surface, #151F2E)',
                border:       '1px solid var(--border, #1E2D42)',
                borderRadius: 5,
                color:        'var(--muted, #6B7D94)',
                cursor:       loading ? 'default' : 'pointer',
                fontSize:     13,
                padding:      '4px 10px',
                opacity:      loading ? 0.5 : 1,
              }}
            >
              {loading ? '…' : '↻'}
            </button>
            <button
              onClick={onClose}
              title="Close (Esc)"
              style={{
                background:   'transparent',
                border:       'none',
                color:        'var(--muted, #6B7D94)',
                cursor:       'pointer',
                fontSize:     18,
                lineHeight:   1,
                padding:      '2px 4px',
              }}
            >
              ✕
            </button>
          </div>
        </div>

        {/* Body */}
        <div style={{ overflowY: 'auto', flex: 1 }}>
          {loading && (
            <div style={{ padding: 40, textAlign: 'center', color: 'var(--muted, #6B7D94)', fontSize: 13 }}>
              Loading…
            </div>
          )}
          {!loading && error && (
            <div style={{ padding: 24, color: '#FF6B6B', fontSize: 13 }}>
              ❌ {error}
            </div>
          )}
          {!loading && !error && rows.length === 0 && (
            <div style={{ padding: 40, textAlign: 'center', color: 'var(--muted, #6B7D94)', fontSize: 13 }}>
              No received POs found.
            </div>
          )}
          {!loading && !error && rows.length > 0 && (
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
              <thead>
                <tr style={{
                  background:  'var(--surface-raised, #0F1A28)',
                  borderBottom:'1px solid var(--border, #1E2D42)',
                  position:    'sticky',
                  top:         0,
                  zIndex:      1,
                }}>
                  {['#', 'PO Ref', 'Supplier', 'Invoice Ref', 'Received', 'Items', 'Open SOs'].map((h, i) => (
                    <th key={h} style={{
                      padding:   '8px 14px',
                      textAlign: i >= 5 ? 'center' : i === 0 ? 'center' : 'left',
                      fontWeight: 600,
                      fontSize:  11,
                      color:     'var(--muted, #6B7D94)',
                      letterSpacing: 0.5,
                      textTransform: 'uppercase',
                      whiteSpace:'nowrap',
                    }}>
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {rows.map((row, idx) => {
                  const isRecent = row.fully_received_date &&
                    (Date.now() - new Date(row.fully_received_date).getTime()) < 6 * 60 * 60 * 1000; // < 6h
                  return (
                    <tr
                      key={row.order_id}
                      style={{
                        borderBottom: '1px solid var(--border, #1E2D42)',
                        background:   isRecent
                          ? 'rgba(200, 146, 42, 0.06)'
                          : idx % 2 === 0
                            ? 'transparent'
                            : 'rgba(255,255,255,0.02)',
                        transition: 'background 0.15s',
                      }}
                      onMouseEnter={e => e.currentTarget.style.background = 'rgba(255,255,255,0.04)'}
                      onMouseLeave={e => e.currentTarget.style.background = isRecent
                        ? 'rgba(200, 146, 42, 0.06)'
                        : idx % 2 === 0 ? 'transparent' : 'rgba(255,255,255,0.02)'}
                    >
                      {/* Row number */}
                      <td style={{ padding: '9px 14px', textAlign: 'center', color: 'var(--muted, #6B7D94)', fontSize: 11 }}>
                        {idx + 1}
                      </td>

                      {/* PO Ref */}
                      <td style={{ padding: '9px 14px', whiteSpace: 'nowrap' }}>
                        <span style={{
                          color:      'var(--accent, #C8922A)',
                          fontWeight: 600,
                          fontFamily: 'monospace',
                          fontSize:   12,
                        }}>
                          {row.order_ref}
                        </span>
                      </td>

                      {/* Supplier */}
                      <td style={{
                        padding:   '9px 14px',
                        color:     'var(--text, #E8EDF4)',
                        maxWidth:  160,
                        overflow:  'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace:'nowrap',
                      }}>
                        {row.supplier || '—'}
                      </td>

                      {/* Invoice Ref */}
                      <td style={{
                        padding:   '9px 14px',
                        color:     'var(--muted, #6B7D94)',
                        fontFamily:'monospace',
                        fontSize:  12,
                      }}>
                        {row.supplier_invoice_ref || '—'}
                      </td>

                      {/* Received date + age */}
                      <td style={{ padding: '9px 14px', whiteSpace: 'nowrap' }}>
                        <div style={{ color: 'var(--text, #E8EDF4)', fontSize: 12 }}>
                          {fmtDateTime(row.fully_received_date)}
                        </div>
                        <div style={{ color: 'var(--muted, #6B7D94)', fontSize: 10, marginTop: 1 }}>
                          {fmtAge(row.fully_received_date)}
                        </div>
                      </td>

                      {/* Item count */}
                      <td style={{ padding: '9px 14px', textAlign: 'center' }}>
                        <span style={{
                          display:      'inline-block',
                          minWidth:     28,
                          padding:      '2px 8px',
                          borderRadius: 12,
                          background:   'rgba(37, 99, 168, 0.2)',
                          color:        '#7DB3E8',
                          fontWeight:   600,
                          fontSize:     12,
                        }}>
                          {row.item_count}
                        </span>
                      </td>

                      {/* Open SO count */}
                      <td style={{ padding: '9px 14px', textAlign: 'center' }}>
                        <span style={{
                          display:      'inline-block',
                          minWidth:     28,
                          padding:      '2px 8px',
                          borderRadius: 12,
                          background:   row.so_count > 0
                            ? 'rgba(46, 125, 50, 0.2)'
                            : 'rgba(255,255,255,0.05)',
                          color:        row.so_count > 0 ? '#6FCF97' : 'var(--muted, #6B7D94)',
                          fontWeight:   600,
                          fontSize:     12,
                        }}>
                          {row.so_count}
                        </span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>

        {/* Footer */}
        <div style={{
          padding:      '10px 20px',
          borderTop:    '1px solid var(--border, #1E2D42)',
          background:   'var(--surface-raised, #0F1A28)',
          display:      'flex',
          alignItems:   'center',
          justifyContent: 'space-between',
          flexShrink:   0,
        }}>
          <span style={{ fontSize: 11, color: 'var(--muted, #6B7D94)' }}>
            {rows.length > 0
              ? `${rows.length} POs · Open SOs = customer orders still waiting for stock from this PO`
              : ''}
          </span>
          <button
            onClick={onClose}
            style={{
              background:   'var(--surface, #151F2E)',
              border:       '1px solid var(--border, #1E2D42)',
              borderRadius: 5,
              color:        'var(--text, #E8EDF4)',
              cursor:       'pointer',
              fontSize:     12,
              padding:      '5px 14px',
            }}
          >
            Close
          </button>
        </div>
      </div>
    </>
  );
}
